import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaChartPie,
    FaUsers,
    FaGraduationCap,
    FaBriefcase,
    FaMoneyBillWave,
    FaBell,
    FaCog,
    FaBars,
    FaSignOutAlt,
    FaChevronLeft,
    FaHistory,
    FaUser,
    FaChartLine,
    FaUserClock,
    FaRegBell,
    FaHome,
    FaFileInvoiceDollar,
    FaHeadset
} from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import { API_URL } from '../../config';
import useAuthGuard from '../../hooks/useAuthGuard';
import './SuperAdminLayout.css';
// import logo from '../../logo.png'; // Assuming logo is in src/ 

const SuperAdminLayout = () => {
    const { user, logout } = React.useContext(AuthContext);
    useAuthGuard(['super_admin', 'tutor', 'recruiter', 'course_admin', 'finance_admin', 'support_staff', 'reports_staff']);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [adminName, setAdminName] = useState('Super Admin');
    const [profileImage, setProfileImage] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const navigate = useNavigate();

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    useEffect(() => {
        const fetchAdminProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const res = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data) {
                    setAdminName(res.data.name || 'Super Admin');
                    setProfileImage(res.data.profileImage || '');
                }
            } catch (err) {
                console.error("Error fetching admin profile:", err);
            }
        };
        fetchAdminProfile();

        const handleProfileUpdate = () => {
            fetchAdminProfile();
        };
        window.addEventListener('profileUpdated', handleProfileUpdate);
        return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
    }, []);

    // Fetch unread broadcast count for tutors and admins
    useEffect(() => {
        if (user?.role !== 'tutor' && user?.role !== 'super_admin') return;
        const fetchUnreadCount = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const res = await axios.get('/api/notifications', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const unread = res.data.filter(n => !n.isRead).length;
                setUnreadCount(unread);
            } catch (err) {
                // silent fail
            }
        };
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 60000);

        // Instant update when Notifications mark items as read
        const handleNotificationsRead = (e) => {
            setUnreadCount(e.detail?.count ?? 0);
        };
        window.addEventListener('notificationsRead', handleNotificationsRead);

        return () => {
            clearInterval(interval);
            window.removeEventListener('notificationsRead', handleNotificationsRead);
        };
    }, [user]);

    const handleLogout = () => {
        logout();
        window.dispatchEvent(new Event('authChange'));
        navigate('/auth');
    };

    const superAdminMenuGroups = [
        {
            heading: 'Navigation',
            items: [
                { path: '/', name: 'Public Home', icon: <FaHome /> }
            ]
        },
        {
            heading: 'Core Dashboard',
            items: [
                { path: '/admin', name: 'Dashboard', icon: <FaChartPie /> }
            ]
        },
        {
            heading: 'Management',
            items: [
                { path: '/admin/users', name: 'Users & Roles', icon: <FaUsers /> },
                { path: '/admin/courses', name: 'Course Management', icon: <FaGraduationCap /> },
                { path: '/admin/jobs', name: 'Job Moderation', icon: <FaBriefcase /> }
            ]
        },
        {
            heading: 'Operations',
            items: [
                { path: '/admin/payments', name: 'Payments', icon: <FaMoneyBillWave /> },
                { path: '/admin/audit', name: 'Audit Log', icon: <FaHistory /> }
            ]
        },
        {
            heading: 'Personal & Support',
            items: [
                { path: '/admin/profile', name: 'Profile', icon: <FaUser /> },
                { path: '/admin/notifications', name: 'Notifications', icon: <FaBell /> },
                { path: '/admin/support', name: 'Support & Alerts', icon: <FaBell /> },
                { path: '/admin/settings', name: 'Settings', icon: <FaCog /> }
            ]
        }
    ];

    const tutorMenuGroups = [
        {
            heading: 'Navigation',
            items: [
                { path: '/', name: 'Public Home', icon: <FaHome /> }
            ]
        },
        {
            heading: 'Main',
            items: [
                { path: '/admin', name: 'Tutor Dashboard', icon: <FaChartPie /> }
            ]
        },
        {
            heading: 'Academics',
            items: [
                { path: '/admin/tutor-courses', name: 'My Courses', icon: <FaGraduationCap /> },
                { path: '/admin/tutor-lms', name: 'LMS Modules', icon: <FaBriefcase /> },
                { path: '/admin/tutor-live-classes', name: 'Live Classes', icon: <FaUsers /> }
            ]
        },
        {
            heading: 'Student Tracking',
            items: [
                { path: '/admin/tutor-assignments', name: 'Assignments & Exams', icon: <FaHistory /> },
                { path: '/admin/tutor-students', name: 'Student Performance', icon: <FaChartLine /> },
                { path: '/admin/tutor-attendance', name: 'Attendance Records', icon: <FaUserClock /> }
            ]
        },
        {
            heading: 'System & Support',
            items: [
                { path: '/admin/tutor-notifications', name: 'Inbox', icon: <FaBell /> },
                { path: '/admin/profile', name: 'My Profile', icon: <FaUser /> },
                { path: '/admin/tutor-support', name: 'Support Center', icon: <FaBell /> }
            ]
        }
    ];

    const menuGroups = user?.role === 'tutor' ? tutorMenuGroups : superAdminMenuGroups;

    return (
        <div className={`super-admin-layout ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>

            {/* Sidebar */}
            <aside className="sidebar">
                {/* Boundary Toggle Button */}
                <button
                    className="sidebar-boundary-toggle"
                    onClick={toggleSidebar}
                    aria-label={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
                >
                    <FaChevronLeft />
                </button>

                <div className="sidebar-header">
                    <div className="logo-container">
                        {isSidebarOpen ? (
                            <h2 className="logo-text">ZenX<span className="accent">Staffing</span></h2>
                        ) : (
                            <h2 className="logo-text">Z<span className="accent">X</span></h2>
                        )}
                    </div>
                </div>

                <nav className="sidebar-nav">
                    {menuGroups.map((group, groupIndex) => (
                        <div key={groupIndex} className="nav-group">
                            <h3 className="sidebar-nav-heading">{group.heading}</h3>
                            <ul>
                                {group.items.map((item, index) => (
                                    <li key={index}>
                                        <NavLink
                                            to={item.path}
                                            end={item.path === '/admin'}
                                            className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                                            title={!isSidebarOpen ? item.name : ""}
                                        >
                                            <span className="nav-icon">{item.icon}</span>
                                            <span className="nav-text">{item.name}</span>
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <button className="logout-btn" onClick={handleLogout}>
                        <span className="nav-icon"><FaSignOutAlt /></span>
                        <span className="nav-text">Sign Out</span>
                    </button>
                </div>
            </aside>

            {/* Main Content Area */}
            <div className="main-content-wrapper">
                <header className="topbar">
                    <div className="topbar-left">
                        {/* Space for Breadcrumbs or Search if needed later */}
                    </div>

                    <div className="topbar-right">
                        {/* Bell Icon for Tutors and Admins */}
                        {(user?.role === 'tutor' || user?.role === 'super_admin') && (
                            <div
                                onClick={() => navigate(user?.role === 'tutor' ? '/admin/tutor-notifications' : '/admin/notifications')}
                                title="Notifications & Broadcasts"
                                style={{
                                    position: 'relative',
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '12px',
                                    background: unreadCount > 0 ? 'rgba(212, 175, 55, 0.12)' : '#F1F5F9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    marginRight: '12px',
                                    border: unreadCount > 0 ? '1px solid rgba(212, 175, 55, 0.4)' : '1px solid #E2E8F0',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <FaBell
                                    size={18}
                                    color={unreadCount > 0 ? 'var(--primary-gold, #D4AF37)' : '#64748B'}
                                />
                                {unreadCount > 0 && (
                                    <span style={{
                                        position: 'absolute',
                                        top: '-6px',
                                        right: '-6px',
                                        background: '#EF4444',
                                        color: '#fff',
                                        borderRadius: '50%',
                                        width: '20px',
                                        height: '20px',
                                        fontSize: '10px',
                                        fontWeight: 800,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '2px solid #fff',
                                        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.4)'
                                    }}>
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </span>
                                )}
                            </div>
                        )}

                        <div className="admin-profile" onClick={() => navigate('/admin/profile')}>
                            <div className="avatar" style={{ overflow: 'hidden' }}>
                                {profileImage ? (
                                    <img
                                        src={`${API_URL}${profileImage}`}
                                        alt="Profile"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    adminName ? adminName.charAt(0).toUpperCase() : 'S'
                                )}
                            </div>
                            <div className="admin-info">
                                <span className="admin-name">{adminName}</span>
                                <span className="admin-role">{user?.role === 'tutor' ? 'Tutor' : 'System Administrator'}</span>
                            </div>
                        </div>
                    </div>
                </header>

                <main className="main-content">
                    <Outlet /> {/* Renders the nested routes */}
                </main>
            </div>
        </div>
    );
};

export default SuperAdminLayout;
